// src/widget/getAppConfig.ts
import { matchPath } from "react-router-dom";
var PREVIEW_TYPE = {
  TESTING: "testing-preview",
  STYLES: "styles-preview"
};
var BASE_URLS = {
  DEFAULT: "/:widgetId",
  PREVIEW: "/:widgetId/:previewType/:previewHash"
};
var DEFAULT_ENDPOINTS = {
  CMS: "https://widgetstate-dev.votenow.tv/v3/state/",
  CONNECT: "https://voteapi-dev.votenow.tv/s2/vote",
  PREVIEW: "https://ssp-api-dev.votenow.tv/v1/widget_previews/"
};
var isPreview = () => {
  var _a;
  const pattern = "/:widgetId/:param/";
  const match = matchPath({ path: pattern, end: false }, window.location.pathname);
  const param = (_a = match == null ? void 0 : match.params) == null ? void 0 : _a.param;
  return param === PREVIEW_TYPE.STYLES || param === PREVIEW_TYPE.TESTING;
};
var getAppConfig = () => {
  const tsReactInitConfig = window.tsReactInitConfig || {};
  const {
    widgetStateUrl = DEFAULT_ENDPOINTS.CMS,
    connectUrl = DEFAULT_ENDPOINTS.CONNECT,
    previewUrl = DEFAULT_ENDPOINTS.PREVIEW
  } = tsReactInitConfig;
  const isPreviewUrl = isPreview();
  const basenamePattern = isPreviewUrl ? BASE_URLS.PREVIEW : BASE_URLS.DEFAULT;
  const match = matchPath({ path: basenamePattern, end: false }, window.location.pathname);
  const urlParams = match == null ? void 0 : match.params;
  const basename = urlParams ? Object.entries(urlParams).reduce(
    (acc, [key, value]) => acc.replace(`:${key}`, value),
    basenamePattern
  ) : "/";
  const appHash = (urlParams == null ? void 0 : urlParams.previewHash) || (urlParams == null ? void 0 : urlParams.widgetId) || "";
  const widgetUrl = isPreviewUrl ? previewUrl : widgetStateUrl;
  const shouldPollWidget = !isPreviewUrl;
  const widgetId = urlParams == null ? void 0 : urlParams.widgetId;
  const isDevMode = (urlParams == null ? void 0 : urlParams.previewType) === PREVIEW_TYPE.STYLES;
  return {
    isPreviewUrl,
    basenamePattern,
    basename,
    urlParams,
    appHash,
    widgetUrl,
    shouldPollWidget,
    widgetId,
    isDevMode,
    connectUrl
  };
};

// src/browser/memoryStorage.ts
var MemoryStorage = class {
  constructor() {
    this._length = 0;
    this.storage = /* @__PURE__ */ new Map();
  }
  getItem(key) {
    return this.storage.get(key) ?? null;
  }
  setItem(key, value) {
    this.storage.set(key, value);
    this._length++;
  }
  removeItem(key) {
    this.storage.delete(key);
    this._length--;
  }
  removeItems(...keys) {
    keys.forEach((key) => this.removeItem(key));
  }
  clear() {
    this.storage.clear();
    this._length = 0;
  }
  key(index) {
    const keys = this.storage.keys();
    return Array.from(keys)[index];
  }
  get length() {
    return this._length;
  }
};

// src/browser/prefixedStorage.ts
var PrefixedStorage = class {
  constructor(prefix = "", storage = null) {
    this.prefix = "";
    this.prefix = prefix;
    try {
      if (!storage) {
        storage = localStorage;
      }
      const testKey = "telescope_tv_test_key";
      storage.setItem(testKey, testKey);
      storage.removeItem(testKey);
      this.storage = storage;
    } catch (e) {
      this.storage = new MemoryStorage();
    }
  }
  getItem(key) {
    const value = this.storage.getItem(this.prefix + key);
    try {
      if (value) {
        const item = JSON.parse(value);
        if (item && item.hasOwnProperty("expiration") && item.hasOwnProperty("value")) {
          const now = new Date().getTime();
          if (item.expiration !== null && now >= item.expiration) {
            this.removeItem(key);
            return null;
          }
          return item.value;
        }
      }
    } catch (e) {
      return value;
    }
    return value;
  }
  setItem(key, value, expiration = null) {
    const item = {
      value,
      expiration
    };
    this.storage.setItem(this.prefix + key, JSON.stringify(item));
  }
  removeItem(key) {
    this.storage.removeItem(this.prefix + key);
  }
  removeItems(...keys) {
    keys.forEach((key) => this.removeItem(key));
  }
  clear() {
    this.storage.clear();
  }
  key(index) {
    const key = this.storage.key(index);
    if (typeof key === "string") {
      return key.substring(this.prefix.length);
    }
    return key;
  }
  get length() {
    return this.storage.length;
  }
};
function createStorage(prefix = "") {
  return new PrefixedStorage(prefix);
}

// src/browser/fixedEncodeURIComponent.ts
var fixedEncodeURIComponent = (uriComponent) => encodeURIComponent(uriComponent).replace(/[!'()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);

// src/browser/convertToQspString.ts
var isValidParamValue = (value) => ["undefined", void 0, null, ""].indexOf(value) < 0;
var convertToQspString = (params, encodeValue = true) => {
  let result = "";
  for (const key in params) {
    const value = params[key];
    if (isValidParamValue(value)) {
      const qspValue = encodeValue ? fixedEncodeURIComponent(value) : `${value}`;
      result += `${key}=${qspValue.trim()}&`;
    }
  }
  return result.slice(0, -1);
};

// src/browser/getQSPs.ts
var getQSPs = () => {
  return window.location.search.slice(1).split("&").reduce((prev, cur) => {
    const [key, value = ""] = cur.split("=");
    if (key === "")
      return prev;
    return { ...prev, [key]: value };
  }, {});
};

// src/browser/getQueryParamByName.ts
var getQueryParamByName = (name) => {
  const regexS = `[\\?&]${name}=([^&#]*)`;
  const regex = new RegExp(regexS);
  const results = regex.exec(window.location.search);
  if (results === null)
    return "";
  return decodeURIComponent(results[1].replace(/\+/g, " "));
};

// src/browser/isWebview.ts
function isWebview() {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const safari = /safari/.test(userAgent);
  const ios = /iphone|ipod|ipad/.test(userAgent);
  const wv = /\bwv\b/.test(userAgent);
  if (ios) {
    return !safari;
  }
  return wv;
}

// src/api/createHash.ts
import Base64 from "crypto-js/enc-base64.js";
import hmacSHA256 from "crypto-js/hmac-sha256.js";
var createHash = (message, key) => {
  const hash = hmacSHA256(message, key);
  return Base64.stringify(hash);
};

// src/object/object.ts
var sortObjectByKeys = (unsortedObj) => {
  return Object.keys(unsortedObj).sort().reduce((acc, key) => {
    acc[key] = unsortedObj[key];
    return acc;
  }, {});
};

// src/string/string.ts
var TRUTHY_VALUES = [true, 1, "true", "1", "enabled", "yes", "active"];
var isTruthy = (value) => {
  return TRUTHY_VALUES.includes(value.toLowerCase().trim());
};

// src/array/sortArrayByProp.ts
var SortDirection = {
  ASCENDING: 1,
  DESCENDING: -1
};
var sortArrayByProp = (arr, key, direction = SortDirection.ASCENDING) => {
  return [...arr].sort((a, b) => {
    const aValue = typeof key(a) === "number" ? key(a).toString() : key(a);
    const bValue = typeof key(b) === "number" ? key(b).toString() : key(b);
    const result = aValue.localeCompare(bValue, void 0, { numeric: true });
    return direction * result;
  });
};
export {
  BASE_URLS,
  DEFAULT_ENDPOINTS,
  MemoryStorage,
  PREVIEW_TYPE,
  PrefixedStorage,
  SortDirection,
  convertToQspString,
  createHash,
  createStorage,
  fixedEncodeURIComponent,
  getAppConfig,
  getQSPs,
  getQueryParamByName,
  isTruthy,
  isWebview,
  sortArrayByProp,
  sortObjectByKeys
};
